import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { WebComponentsLoaderService } from '@shared/webcomponents/webcomponents-loader/webcomponents-loader.service';
import { distinctUntilChanged, filter } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from '@shared/services';
import { nativeFetch } from '@shared/services/rocos-sdk-client/native-fetch';

// Legacy status-dot sizes (see status-dot.component.scss) mapped to the exact pixel diameter the
// shared web component takes. Keeps the migrated dot the same footprint as the bespoke one.
const SIZE_PX: Record<'large' | 'medium' | 'small', number> = {
  large: 64,
  medium: 16,
  small: 12,
};

/**
 * Wrapper for the shared robot status dot web component (`rc-cw-robot-status-dot`), served from
 * custom-widgets. Unlike the omniviewer wrappers it takes `projectId`/`callsign` as inputs, since
 * the dot is rendered per-robot (e.g. in robot lists), not just for the app's current robot.
 *
 * `size`/`dark` mirror the bespoke dot's inputs and are passed through the widget `config` (the
 * component maps the legacy size names to exact pixels and themes the neutral grey for the surface).
 */
@Component({
  selector: 'app-cw-robot-status-dot',
  template: `<rc-cw-robot-status-dot
    [attr.projectid]="projectId"
    [attr.callsign]="callsign"
    [attr.sdk]="{
        token: token$ | async,
        url: url,
        transport: transport,
      } | json"
    [attr.config]="{ size: sizePx, dark: dark } | json"
    [sdkFetch]="sdkFetch"
  ></rc-cw-robot-status-dot>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CWRobotStatusDotComponent {
  @Input() projectId: string;
  @Input() callsign: string;
  @Input() size: 'large' | 'medium' | 'small' = 'medium';
  @Input() dark = false;

  protected get sizePx(): number {
    return SIZE_PX[this.size] ?? SIZE_PX.medium;
  }

  token$ = this.authService.token$.pipe(filter(Boolean), distinctUntilChanged());

  url = new URL(environment.api.url).hostname;
  // Pre-RUM fetch injected as a DOM property so the widget SDK reads it for both transports
  // (functions can't ride the serialized `sdk` attribute). See native-fetch.ts.
  protected readonly sdkFetch = nativeFetch;
  // Telemetry transport (grpc-web on dev/stage; SDK default grpc-web when unset).
  protected readonly transport = environment.api.transport;

  constructor(
    private readonly webComponentLoader: WebComponentsLoaderService,
    private readonly authService: AuthService,
  ) {
    this.webComponentLoader.loadDefaultComponents();
  }
}
